/**
 * SlotDrillPicker — filtered drill picker for a specific template slot.
 *
 * Shows two ranked lists side by side (posture-based & measurement-based) so the
 * practitioner can choose from both suggestion methods. Read-only posture filter chips
 * are displayed for transparency.
 *
 * @see docs/features/260331-automated-driller-mvp/KICKOFF_PROMPT.md §5 (Per-block drill swap)
 */

import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Search, CheckCircle2, Brain, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Drill } from '@/components/drill-library/types/drill-types';
import type { DrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import type { SlotCategory } from './lib/suggest-drills';
import { suggestDrillsForClient, suggestDrillsForClientAI } from './lib/suggest-drills';
import type { SlotSuggestions } from './hooks/use-auto-fill';

interface SlotDrillPickerProps {
  isOpen: boolean;
  onClose: () => void;
  slotLabel: string;
  slotCategory: SlotCategory;
  currentDrillId?: string;
  filters: DrillSuggestionFilters | null;
  drills: Drill[];
  /** Pre-computed dual suggestions (optional; falls back to on-demand computation). */
  slotSuggestions?: SlotSuggestions | null;
  onSelect: (drillId: string) => void;
}

/** Small drill row card */
const DrillRow: React.FC<{
  drill: Drill;
  score: number;
  isCurrent: boolean;
  showScore?: boolean;
  onSelect: (id: string) => void;
}> = ({ drill, score, isCurrent, showScore, onSelect }) => (
  <button
    onClick={() => onSelect(drill.id)}
    className={cn(
      'w-full text-left p-3 rounded-lg border transition-colors flex items-start gap-3',
      isCurrent ? 'border-primary bg-primary/5' : 'hover:bg-accent hover:border-primary/40'
    )}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm truncate">{drill.name}</span>
        {isCurrent && <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />}
      </div>
      {drill.description && (
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{drill.description}</p>
      )}
      <div className="flex flex-wrap gap-1 mt-1.5">
        {drill.tags?.['Phase'] && (
          <Badge variant="secondary" className="text-xs px-1.5">
            {Array.isArray(drill.tags['Phase']) ? drill.tags['Phase'][0] : drill.tags['Phase']}
          </Badge>
        )}
        {drill.mainCategory && (
          <Badge variant="outline" className="text-xs px-1.5">
            {drill.mainCategory}
          </Badge>
        )}
      </div>
    </div>
    {showScore && score > 0 && (
      <span className="text-xs text-muted-foreground mt-1 flex-shrink-0">{score}pts</span>
    )}
  </button>
);

const SlotDrillPicker: React.FC<SlotDrillPickerProps> = ({
  isOpen,
  onClose,
  slotLabel,
  slotCategory,
  currentDrillId,
  filters,
  drills,
  slotSuggestions,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'posture' | 'ai' | 'all'>('posture');

  // Posture-based ranked list
  const postureRanked = useMemo(() => {
    if (!filters) return drills.map((d) => ({ drill: d, score: 0 }));
    return suggestDrillsForClient(filters, slotCategory, drills, 30);
  }, [filters, slotCategory, drills]);

  // AI / measurement-based ranked list
  const aiRanked = useMemo(() => {
    if (!filters) return drills.map((d) => ({ drill: d, score: 0 }));
    // Reuse pre-computed if available; otherwise compute now (no analysis items = fallback)
    return suggestDrillsForClientAI(filters, slotCategory, drills, [], 30);
  }, [filters, slotCategory, drills]);

  // All drills (simple name sort) for the "browse all" tab
  const allSorted = useMemo(
    () => [...drills].sort((a, b) => a.name.localeCompare(b.name)),
    [drills]
  );

  const applySearch = <T extends { drill: Drill }>(list: T[]): T[] => {
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(({ drill }) => drill.name.toLowerCase().includes(q));
  };

  const filteredPosture = useMemo(() => applySearch(postureRanked), [postureRanked, searchTerm]);
  const filteredAI = useMemo(() => applySearch(aiRanked), [aiRanked, searchTerm]);
  const filteredAll = useMemo(
    () =>
      applySearch(allSorted.map((drill) => ({ drill, score: 0 }))),
    [allSorted, searchTerm]
  );

  const handleSelect = (drillId: string) => {
    onSelect(drillId);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-4 pt-5 pb-3 border-b">
          <DialogTitle className="text-sm leading-snug">
            Pick a drill for <span className="font-bold">{slotLabel}</span>
          </DialogTitle>

          {/* Read-only filter chips */}
          {filters && (
            <div className="flex flex-wrap gap-1 pt-1.5">
              {filters.phaseTag && (
                <Badge variant="secondary" className="text-xs">{filters.phaseTag}</Badge>
              )}
              {filters.postureLabels.map((l) => (
                <Badge key={l} variant="outline" className="text-xs">{l}</Badge>
              ))}
              {filters.movementLimitations.slice(0, 2).map((lim) => (
                <Badge key={lim} variant="outline" className="text-xs text-muted-foreground">
                  {lim.length > 35 ? `${lim.slice(0, 35)}…` : lim}
                </Badge>
              ))}
              {filters.movementLimitations.length > 2 && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  +{filters.movementLimitations.length - 2}
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        {/* Search */}
        <div className="px-4 py-2.5 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search drills…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Tabs: Posture | AI | All */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'posture' | 'ai' | 'all')}
          className="flex flex-col flex-1 min-h-0"
        >
          <TabsList className="mx-4 mt-2 mb-1 w-auto h-8 grid grid-cols-3">
            <TabsTrigger value="posture" className="text-xs gap-1">
              <Sparkles className="h-3 w-3" />
              Posture
            </TabsTrigger>
            <TabsTrigger value="ai" className="text-xs gap-1">
              <Brain className="h-3 w-3" />
              Measurement
            </TabsTrigger>
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
          </TabsList>

          <TabsContent value="posture" className="flex-1 min-h-0 m-0">
            <ScrollArea className="h-full px-4 py-2">
              <DrillList
                items={filteredPosture}
                currentDrillId={currentDrillId}
                showScore={!!filters}
                onSelect={handleSelect}
              />
            </ScrollArea>
          </TabsContent>

          <TabsContent value="ai" className="flex-1 min-h-0 m-0">
            <ScrollArea className="h-full px-4 py-2">
              <p className="text-xs text-muted-foreground mb-2 px-1">
                Prioritises drills that address the client's worst failing assessment measurements.
              </p>
              <DrillList
                items={filteredAI}
                currentDrillId={currentDrillId}
                showScore={!!filters}
                onSelect={handleSelect}
              />
            </ScrollArea>
          </TabsContent>

          <TabsContent value="all" className="flex-1 min-h-0 m-0">
            <ScrollArea className="h-full px-4 py-2">
              <DrillList
                items={filteredAll}
                currentDrillId={currentDrillId}
                showScore={false}
                onSelect={handleSelect}
              />
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <div className="px-4 py-3 border-t flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DrillList: React.FC<{
  items: Array<{ drill: Drill; score: number }>;
  currentDrillId?: string;
  showScore: boolean;
  onSelect: (id: string) => void;
}> = ({ items, currentDrillId, showScore, onSelect }) => {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No drills match the current filters.
      </p>
    );
  }
  return (
    <div className="space-y-2 pb-2">
      {items.map(({ drill, score }) => (
        <DrillRow
          key={drill.id}
          drill={drill}
          score={score}
          isCurrent={drill.id === currentDrillId}
          showScore={showScore}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

export default SlotDrillPicker;
